/**
 * Character Counter Controller
 *
 * A Stimulus controller that provides real-time character counting for text inputs.
 * It displays the current character count and enforces a maximum length limit.
 *
 * Usage:
 * 1. Add the controller to a parent element:
 *    <div data-controller="character-counter">
 *
 * 2. Add the input target to your textarea/input:
 *    <textarea data-character-counter-target="input" maxlength="500">
 *
 * 3. Add the counter target to display the count:
 *    <span data-character-counter-target="counter">0</span> / 500
 *
 * Features:
 * - Real-time character counting
 * - Works with both textarea and input elements
 * - Automatically updates on input
 * - Cleans up event listeners on disconnect
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  /**
   * Stimulus targets
   * @property {HTMLTextAreaElement|HTMLInputElement} input - The input element to count characters from
   * @property {HTMLElement} counter - The element to display the current count
   */
  static targets = ["input", "counter"];

  /**
   * Stimulus values
   * @property {number} limit - The maximum number of characters allowed (optional)
   */
  static values = {
    limit: Number,
  };

  /**
   * Called when the controller is connected to the DOM
   * Sets up initial count and event listener
   */
  connect() {
    if (this.hasInputTarget) {
      this.updateCounter();
      this.inputTarget.addEventListener("input", this.updateCounter);
    }
  }

  /**
   * Called when the controller is disconnected from the DOM
   * Cleans up event listener to prevent memory leaks
   */
  disconnect() {
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("input", this.updateCounter);
    }
  }

  /**
   * Updates the character counter display
   * Called whenever the input value changes
   */
  updateCounter = () => {
    const length = this.inputTarget.value.length;
    this.counterTarget.textContent = length;
  };
}
